import { useEffect, useMemo, useState } from 'react'

import Toast from './components/feedback/Toast'
import Sidebar from './components/layout/Sidebar'
import Topbar from './components/layout/Topbar'
import { API_URL, PAGE_SIZE, emptyFilters, emptyForm } from './constants/app'
import DeletePatientModal from './modals/DeletePatientModal'
import DeleteExpenseModal from './modals/DeleteExpenseModal'
import PatientFilterModal from './modals/PatientFilterModal'
import PatientModal from './modals/PatientModal'
import ExpenseModal from './modals/ExpenseModal'
import ExportDateFilterModal from './modals/ExportDateFilterModal'
import AuthPage from './pages/AuthPage'
import DashboardPage from './pages/DashboardPage'
import PatientsPage from './pages/PatientsPage'
import ExpensesPage, { ActionMenuOverlay as ExpenseActionMenuOverlay } from './pages/ExpensesPage'
import { ActionMenuOverlay as PatientActionMenuOverlay } from './pages/PatientsPage'
import { cleanCPF, formatCPF, formatPaymentType } from './utils/formatters'
import { formatApiError, formatRequestError, readErrorPayload } from './utils/errors'
import { clearStoredSession, readStoredSession, storeSession } from './utils/session'

function formatDateInputValue(value = new Date()) {
  const date = new Date(value)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function App() {
  const [authSession, setAuthSession] = useState(() => readStoredSession())
  const [authView, setAuthView] = useState('login')
  const [activeTab, setActiveTab] = useState('dashboard')
  const [patients, setPatients] = useState([])
  const [dashboard, setDashboard] = useState(null)
  const [dashboardPeriod, setDashboardPeriod] = useState('month')
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: PAGE_SIZE,
    total: 0,
    total_pages: 1,
  })
  const [form, setForm] = useState(emptyForm)
  const [patientFilters, setPatientFilters] = useState(emptyFilters)
  const [patientSearch, setPatientSearch] = useState('')
  const [activeModal, setActiveModal] = useState(null)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [actionMenu, setActionMenu] = useState(null)
  const [exportDateFilter, setExportDateFilter] = useState({ start_date: '', end_date: '' })
  const [isLoadingPatients, setIsLoadingPatients] = useState(false)
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isExportingPatients, setIsExportingPatients] = useState(false)
  const [isExportingExpenses, setIsExportingExpenses] = useState(false)
  const [isImportingPatients, setIsImportingPatients] = useState(false)
  const [error, setError] = useState('')
  const [modalError, setModalError] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [patientSuggestions, setPatientSuggestions] = useState([])
  const [isSearchingPatients, setIsSearchingPatients] = useState(false)
  
  // Expenses state
  const [expenses, setExpenses] = useState([])
  const [allExpenses, setAllExpenses] = useState([])
  const [expensesPagination, setExpensesPagination] = useState({
    page: 1,
    page_size: PAGE_SIZE,
    total: 0,
    total_pages: 1,
  })
  const [expenseForm, setExpenseForm] = useState({ category: '', description: '', value: '', date: '' })
  const [expenseSearch, setExpenseSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [expensesPeriod, setExpensesPeriod] = useState('month')
  const [selectedExpense, setSelectedExpense] = useState(null)
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false)

  const filteredPatients = useMemo(() => {
    const search = patientSearch.trim().toLowerCase()

    return patients.filter((patient) =>
      [patient.name, patient.procedure, formatPaymentType(patient.payment_type)]
        .join(' ')
        .toLowerCase()
        .includes(search) &&
      (!patientFilters.name ||
        patient.name.toLowerCase().includes(patientFilters.name.trim().toLowerCase())) &&
      (!patientFilters.procedure || patient.procedure === patientFilters.procedure) &&
      (!patientFilters.payment_type || patient.payment_type === patientFilters.payment_type) &&
      (!patientFilters.min_value || Number(patient.value) >= Number(patientFilters.min_value)) &&
      (!patientFilters.max_value || Number(patient.value) <= Number(patientFilters.max_value)) &&
      (!patientFilters.duration_minutes ||
        Number(patient.duration_minutes) <= Number(patientFilters.duration_minutes)) &&
      (!patientFilters.start_date ||
        new Date(patient.created_at) >= new Date(`${patientFilters.start_date}T00:00:00`)) &&
      (!patientFilters.end_date ||
        new Date(patient.created_at) <= new Date(`${patientFilters.end_date}T23:59:59`)),
    )
  }, [patientFilters, patientSearch, patients])

  const revenueByType = useMemo(() => {
    const items = dashboard?.revenue_by_payment_type || []
    const total = items.reduce((sum, item) => sum + item.revenue, 0)
    const particular = items.find((item) => item.payment_type === 'particular') || {
      revenue: 0,
      count: 0,
    }
    const convenio = items.find((item) => item.payment_type === 'convenio') || {
      revenue: 0,
      count: 0,
    }
    const particularPercent = total ? Math.round((particular.revenue / total) * 100) : 0

    return { convenio, particular, particularPercent, total }
  }, [dashboard])

  const topProcedureMax = useMemo(() => {
    if (!dashboard?.top_procedures?.length) return 1
    return Math.max(...dashboard.top_procedures.map((item) => item.revenue), 1)
  }, [dashboard])

  useEffect(() => {
    if (!authSession) return
    loadPatients(1)
    loadDashboard('month')
    loadExpenses(1)
  }, [authSession])

  // Recarregar expenses quando mudar para aba de expenses
  useEffect(() => {
    if (!authSession) return
    if (activeTab === 'expenses') {
      loadExpenses(1)
    }
  }, [activeTab, authSession])

  // Recarregar expenses quando filtros mudarem (apenas tabela paginada)
  useEffect(() => {
    if (!authSession) return
    if (activeTab === 'expenses') {
      loadExpenses(1)
    }
  }, [expenseSearch, categoryFilter, authSession])

  useEffect(() => {
    if (!authSession) return
    if (activeTab === 'dashboard' && dashboardPeriod !== 'month') {
      setDashboardPeriod('month')
      loadDashboard('month')
    }
  }, [activeTab, authSession])

  useEffect(() => {
    if (!authSession || activeModal !== 'create') {
      setPatientSuggestions([])
      setIsSearchingPatients(false)
      return undefined
    }

    const query = form.name.trim()
    if (query.length < 2) {
      setPatientSuggestions([])
      setIsSearchingPatients(false)
      return undefined
    }

    let isCancelled = false
    const timeoutId = window.setTimeout(async () => {
      setIsSearchingPatients(true)

      try {
        const data = await request(`/patients/search?query=${encodeURIComponent(query)}&limit=6`)
        if (isCancelled) return

        const exactMatch = data.length === 1 && data[0].name.trim().toLocaleLowerCase() === query.toLocaleLowerCase()
        if (exactMatch) {
          applyPatientSuggestion(data[0])
        } else {
          setPatientSuggestions(data)
        }
      } catch {
        if (!isCancelled) setPatientSuggestions([])
      } finally {
        if (!isCancelled) setIsSearchingPatients(false)
      }
    }, 250)

    return () => {
      isCancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [activeModal, authSession, form.name])

  useEffect(() => {
    if (!toastMessage) return undefined

    const timeoutId = window.setTimeout(() => {
      setToastMessage('')
    }, 3500)

    return () => window.clearTimeout(timeoutId)
  }, [toastMessage])

  function forceLogout() {
    clearStoredSession()
    setActionMenu(null)
    setActiveModal(null)
    setSelectedPatient(null)
    setForm(emptyForm)
    setAuthSession(null)
    setAuthView('login')
    setActiveTab('dashboard')
    setError('')
  }

  async function request(path, options = {}) {
    const { headers: optionHeaders, responseType, ...restOptions } = options
    const headers = {
      ...optionHeaders,
    }

    const isFormData = typeof FormData !== 'undefined' && restOptions.body instanceof FormData
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    if (authSession?.access_token) {
      headers.Authorization = `Bearer ${authSession.access_token}`
    }

    let response
    try {
      response = await fetch(`${API_URL}${path}`, {
        ...restOptions,
        headers,
      })
    } catch (error) {
      throw new Error(formatRequestError(error))
    }

    if (response.status === 401) {
      forceLogout()
      throw new Error('Sessão expirada. Faça login novamente.')
    }

    if (!response.ok) {
      const payload = await readErrorPayload(response)
      throw new Error(formatApiError(payload))
    }

    if (response.status === 204) {
      return null
    }

    if (responseType === 'blob') {
      return response.blob()
    }

    return response.json()
  }

  async function loadPatients(page = pagination.page) {
    setIsLoadingPatients(true)
    setError('')

    try {
      const data = await request(`/patients?page=${page}&page_size=${PAGE_SIZE}`)
      setPatients(data.items)
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages,
      })
    } catch (err) {
      setError(formatRequestError(err))
    } finally {
      setIsLoadingPatients(false)
    }
  }

  async function loadDashboard(period = dashboardPeriod) {
    setIsLoadingDashboard(true)
    setError('')

    try {
      const data = await request(`/dashboard?period=${period}`)
      setDashboard(data)
    } catch (err) {
      setError(formatRequestError(err))
    } finally {
      setIsLoadingDashboard(false)
    }
  }

  async function changeDashboardPeriod(period) {
    setDashboardPeriod(period)
    await loadDashboard(period)
  }

  function updateForm(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }))
  }

  function applyPatientSuggestion(patient) {
    setForm((current) => ({
      ...current,
      name: patient.name,
      cpf: cleanCPF(patient.cpf) || '',
    }))
    setPatientSuggestions([])
  }

  function openCreateModal() {
    setSelectedPatient(null)
    setForm(emptyForm)
    setPatientSuggestions([])
    setModalError('')
    setActiveModal('create')
  }

  function openEditModal(patient) {
    setActionMenu(null)
    setSelectedPatient(patient)
    setForm({
      name: patient.name,
      procedure: patient.procedure,
      value: String(patient.value),
      duration_minutes: String(patient.duration_minutes),
      payment_type: patient.payment_type,
      cpf: cleanCPF(patient.cpf) || '',
      date: patient.created_at ? formatDateInputValue(patient.created_at) : '',
    })
    setModalError('')
    setActiveModal('edit')
  }

  function openDeleteModal(patient) {
    setActionMenu(null)
    setSelectedPatient(patient)
    setModalError('')
    setActiveModal('delete')
  }

  function openFilterModal() {
    setActionMenu(null)
    setActiveModal('filter')
  }

  function toggleActionMenu(item, event, type = 'patient') {
    const rect = event.currentTarget.getBoundingClientRect()
    const menuWidth = 144
    const left = Math.min(Math.max(12, rect.right - menuWidth), window.innerWidth - menuWidth - 12)

    setActionMenu((current) => {
      const currentItem = type === 'patient' ? current?.patient : current?.expense
      if (currentItem?.id === item.id) {
        return null
      }
      return {
        left,
        [type]: item,
        top: rect.bottom + 8,
      }
    })
  }

  function closeModal() {
    setActiveModal(null)
    setSelectedPatient(null)
    setForm(emptyForm)
    setPatientSuggestions([])
    setModalError('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setIsSubmitting(true)
    setModalError('')

    const payload = {
      name: form.name.trim(),
      procedure: form.procedure.trim(),
      value: Number(form.value),
      duration_minutes: Number(form.duration_minutes),
      payment_type: form.payment_type,
      cpf: form.payment_type === 'particular' ? cleanCPF(form.cpf) || null : null,
      created_at: form.date ? new Date(`${form.date}T00:00:00`).toISOString() : new Date().toISOString(),
    }

    try {
      const isEditing = activeModal === 'edit' && selectedPatient
      await request(isEditing ? `/patients/${selectedPatient.id}` : '/patients', {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      })
      setForm(emptyForm)
      setActiveModal(null)
      setSelectedPatient(null)
      setModalError('')
      await Promise.all([loadPatients(1), loadDashboard(dashboardPeriod)])
      setActiveTab('patients')
      setToastMessage(isEditing ? 'Paciente editado com sucesso.' : 'Paciente cadastrado com sucesso.')
    } catch (err) {
      setModalError(formatRequestError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDeletePatient() {
    if (!selectedPatient) return

    setIsSubmitting(true)
    setModalError('')

    try {
      await request(`/patients/${selectedPatient.id}`, {
        method: 'DELETE',
      })
      closeModal()
      await Promise.all([loadPatients(1), loadDashboard(dashboardPeriod)])
      setToastMessage('Paciente excluído com sucesso.')
    } catch (err) {
      setModalError(formatRequestError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleExportPatients() {
    setActiveModal('exportDateFilter')
  }

  async function executeExportPatients(dateFilter = null) {
    setIsExportingPatients(true)
    setError('')

    try {
      const filterToUse = dateFilter || exportDateFilter
      const params = new URLSearchParams()
      if (filterToUse.start_date) {
        params.append('start_date', filterToUse.start_date)
      }
      if (filterToUse.end_date) {
        params.append('end_date', filterToUse.end_date)
      }

      const queryString = params.toString()
      const endpoint = `/patients/export${queryString ? `?${queryString}` : ''}`
      
      const blob = await request(endpoint, { responseType: 'blob' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'pacientes.xlsx'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      setToastMessage('Planilha exportada com sucesso.')
      closeModal()
    } catch (err) {
      setError(formatRequestError(err, 'Não foi possível exportar a planilha.'))
    } finally {
      setIsExportingPatients(false)
    }
  }

  function handleExportExpenses() {
    setActiveModal('exportExpensesDateFilter')
  }

  async function executeExportExpenses(dateFilter = null) {
    setIsExportingExpenses(true)
    setError('')

    try {
      const filterToUse = dateFilter || exportDateFilter
      const params = new URLSearchParams()
      if (filterToUse.start_date) {
        params.append('start_date', filterToUse.start_date)
      }
      if (filterToUse.end_date) {
        params.append('end_date', filterToUse.end_date)
      }

      const queryString = params.toString()
      const endpoint = `/expenses/export${queryString ? `?${queryString}` : ''}`

      const blob = await request(endpoint, { responseType: 'blob' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'saidas.xlsx'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      setToastMessage('Planilha de saídas exportada com sucesso.')
      closeModal()
    } catch (err) {
      setError(formatRequestError(err, 'Não foi possível exportar a planilha de saídas.'))
    } finally {
      setIsExportingExpenses(false)
    }
  }

  function handleImportPatients() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    input.onchange = async (event) => {
      const file = event.target.files?.[0]
      if (!file) return

      setIsImportingPatients(true)
      setError('')

      try {
        const formData = new FormData()
        formData.append('file', file)
        const result = await request('/patients/import', {
          method: 'POST',
          body: formData,
        })

        await Promise.all([loadPatients(1), loadDashboard(dashboardPeriod)])

        if (result.imported && result.skipped) {
          setToastMessage(
            `${result.imported} paciente(s) importado(s). ${result.skipped} linha(s) ignorada(s).`,
          )
        } else if (result.imported) {
          setToastMessage(`${result.imported} paciente(s) importado(s) com sucesso.`)
        } else {
          setError(
            result.errors?.[0] ||
              'Nenhum paciente válido encontrado na planilha.',
          )
        }
      } catch (err) {
        setError(formatRequestError(err, 'Não foi possível importar a planilha.'))
      } finally {
        setIsImportingPatients(false)
      }
    }
    input.click()
  }

  // Expenses functions
  async function loadExpenses(page = expensesPagination.page) {
    setIsLoadingExpenses(true)
    setError('')

    try {
      // Passar filtros de busca e categoria para a API
      const searchParam = expenseSearch ? `&search=${encodeURIComponent(expenseSearch)}` : ''
      const categoryParam = categoryFilter ? `&category=${encodeURIComponent(categoryFilter)}` : ''
      const data = await request(`/expenses?page=${page}&page_size=${PAGE_SIZE}${searchParam}${categoryParam}`)
      setExpenses(data.items)
      setExpensesPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages,
      })
      
      // Usar os mesmos dados da tabela para os cards (evita requisições extras)
      if (page === 1) {
        setAllExpenses(data.items)
      }
    } catch (err) {
      setError(formatRequestError(err))
    } finally {
      setIsLoadingExpenses(false)
    }
  }

  function updateExpenseForm(field, value) {
    setExpenseForm((current) => ({
      ...current,
      [field]: value,
    }))
  }

  function openCreateExpenseModal() {
    setSelectedExpense(null)
    setExpenseForm({ category: '', description: '', value: '', date: formatDateInputValue() })
    setModalError('')
    setActiveModal('create-expense')
  }

  function openEditExpenseModal(expense) {
    setActionMenu(null)
    setSelectedExpense(expense)
    setExpenseForm({
      category: expense.category,
      description: expense.description,
      value: String(expense.value),
      date: expense.created_at ? new Date(expense.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    })
    setModalError('')
    setActiveModal('edit-expense')
  }

  function openDeleteExpenseModal(expense) {
    setActionMenu(null)
    setSelectedExpense(expense)
    setModalError('')
    setActiveModal('delete-expense')
  }

  async function handleExpenseSubmit(event) {
    event.preventDefault()
    setIsSubmitting(true)
    setModalError('')

    const payload = {
      category: expenseForm.category,
      description: expenseForm.description,
      value: Number(expenseForm.value),
      created_at: expenseForm.date ? new Date(expenseForm.date + 'T00:00:00').toISOString() : new Date().toISOString(),
    }

    try {
      const isEditing = activeModal === 'edit-expense' && selectedExpense
      await request(isEditing ? `/expenses/${selectedExpense.id}` : '/expenses', {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      })
      setExpenseForm({ category: '', description: '', value: '', date: '' })
      setActiveModal(null)
      setSelectedExpense(null)
      setModalError('')
      await loadExpenses(1)
      setToastMessage(isEditing ? 'Saída editada com sucesso.' : 'Saída cadastrada com sucesso.')
    } catch (err) {
      setModalError(formatRequestError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDeleteExpense() {
    if (!selectedExpense) return

    setIsSubmitting(true)
    setModalError('')

    try {
      await request(`/expenses/${selectedExpense.id}`, {
        method: 'DELETE',
      })
      closeModal()
      await loadExpenses(1)
      setToastMessage('Saída excluída com sucesso.')
    } catch (err) {
      setModalError(formatRequestError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleAuthenticate(session, remember = true) {
    setAuthSession(session)
    storeSession(session, remember)
  }

  function handleLogout() {
    forceLogout()
  }

  if (!authSession) {
    return (
      <AuthPage
        mode={authView}
        onAuthenticate={handleAuthenticate}
        onModeChange={setAuthView}
      />
    )
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#F9F9F9] text-[#1A1A1A]">
      <div className="min-h-screen lg:pl-[240px]">
        <Sidebar activeTab={activeTab} onChange={setActiveTab} onLogout={handleLogout} />

        <section className="min-w-0">
          <Topbar activeTab={activeTab} onChange={setActiveTab} onLogout={handleLogout} />

          <div className="max-w-full px-4 pb-6 pt-6 sm:px-6 lg:px-8 lg:pt-8">
            {error ? (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {error}
              </div>
            ) : null}

            {activeTab === 'dashboard' ? (
              <DashboardPage
                dashboard={dashboard}
                dashboardPeriod={dashboardPeriod}
                isLoading={isLoadingDashboard}
                onPeriodChange={changeDashboardPeriod}
                revenueByType={revenueByType}
                topProcedureMax={topProcedureMax}
              />
            ) : activeTab === 'patients' ? (
              <PatientsPage
                filteredPatients={filteredPatients}
                isExporting={isExportingPatients}
                isImporting={isImportingPatients}
                isLoading={isLoadingPatients}
                onDeletePatient={openDeleteModal}
                onEditPatient={openEditModal}
                onExportPatients={handleExportPatients}
                onFilterPatients={openFilterModal}
                onImportPatients={handleImportPatients}
                onNewPatient={openCreateModal}
                onPageChange={loadPatients}
                onToggleActionMenu={(patient, event) => toggleActionMenu(patient, event, 'patient')}
                openActionPatientId={actionMenu?.patient.id}
                pagination={pagination}
                patientSearch={patientSearch}
                setPatientSearch={setPatientSearch}
              />
            ) : activeTab === 'expenses' ? (
              <ExpensesPage
                expenses={expenses}
                allExpenses={allExpenses}
                isExporting={isExportingExpenses}
                isLoading={isLoadingExpenses}
                onDeleteExpense={openDeleteExpenseModal}
                onEditExpense={openEditExpenseModal}
                onExportExpenses={handleExportExpenses}
                onFilterExpenses={() => {}}
                onNewExpense={openCreateExpenseModal}
                onPageChange={loadExpenses}
                onToggleActionMenu={(expense, event) => toggleActionMenu(expense, event, 'expense')}
                openActionExpenseId={actionMenu?.expense?.id}
                pagination={expensesPagination}
                expenseSearch={expenseSearch}
                setExpenseSearch={setExpenseSearch}
                categoryFilter={categoryFilter}
                setCategoryFilter={setCategoryFilter}
                expensesPeriod={expensesPeriod}
                onExpensesPeriodChange={setExpensesPeriod}
              />
            ) : null}
          </div>
        </section>
      </div>

      {actionMenu ? (
        actionMenu.patient ? (
          <PatientActionMenuOverlay
            menu={actionMenu}
            onClose={() => setActionMenu(null)}
            onDelete={openDeleteModal}
            onEdit={openEditModal}
          />
        ) : (
          <ExpenseActionMenuOverlay
            menu={actionMenu}
            onClose={() => setActionMenu(null)}
            onDelete={openDeleteExpenseModal}
            onEdit={openEditExpenseModal}
          />
        )
      ) : null}

      {activeModal === 'create' || activeModal === 'edit' ? (
        <PatientModal
          error={modalError}
          form={form}
          isSubmitting={isSubmitting}
          isSearchingPatients={isSearchingPatients}
          mode={activeModal}
          onChange={updateForm}
          onClose={closeModal}
          onSelectPatient={applyPatientSuggestion}
          onSubmit={handleSubmit}
          patientSuggestions={patientSuggestions}
        />
      ) : null}

      {activeModal === 'filter' ? (
        <PatientFilterModal
          filters={patientFilters}
          onApply={(filters) => {
            setPatientFilters(filters)
            setActiveModal(null)
          }}
          onClear={() => {
            setPatientFilters(emptyFilters)
            setActiveModal(null)
          }}
          onClose={closeModal}
        />
      ) : null}

      {activeModal === 'exportDateFilter' ? (
        <ExportDateFilterModal
          title="Exportar pacientes"
          onApply={async (dateFilter) => {
            console.log('DEBUG: App received date filter:', dateFilter)
            setExportDateFilter(dateFilter)
            await executeExportPatients(dateFilter)
          }}
          onClose={closeModal}
        />
      ) : null}

      {activeModal === 'exportExpensesDateFilter' ? (
        <ExportDateFilterModal
          title="Exportar saídas"
          fieldLabel="Período das saídas"
          isSubmitting={isExportingExpenses}
          onApply={async (dateFilter) => {
            setExportDateFilter(dateFilter)
            await executeExportExpenses(dateFilter)
          }}
          onClose={closeModal}
        />
      ) : null}

      {activeModal === 'delete' && selectedPatient ? (
        <DeletePatientModal
          error={modalError}
          isSubmitting={isSubmitting}
          patient={selectedPatient}
          onClose={closeModal}
          onConfirm={handleDeletePatient}
        />
      ) : null}

      {activeModal === 'create-expense' || activeModal === 'edit-expense' ? (
        <ExpenseModal
          error={modalError}
          form={expenseForm}
          isSubmitting={isSubmitting}
          mode={activeModal === 'edit-expense' ? 'edit' : 'create'}
          onChange={updateExpenseForm}
          onClose={closeModal}
          onSubmit={handleExpenseSubmit}
        />
      ) : null}

      {activeModal === 'delete-expense' && selectedExpense ? (
        <DeleteExpenseModal
          error={modalError}
          isSubmitting={isSubmitting}
          expense={selectedExpense}
          onClose={closeModal}
          onConfirm={handleDeleteExpense}
        />
      ) : null}

      <Toast message={toastMessage} onClose={() => setToastMessage('')} />
    </main>
  )
}

export default App
